<template>
  <modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="$emit('close')"
  >
    <div
      v-if="!success"
      class="flex justify-center px-4"
    >
      <div class="relative rounded bg-white p-8">
        <img
          class="absolute right-4 top-4 h-4 w-4"
          src="@/assets/close-icon.svg"
          alt="close icon"
          @click="$emit('close')"
        />

        <h3 class="text-left text-3xl text-dark-gray">Add New Company</h3>
        <h4 class="my-4 text-left text-dark-blue">
          Enter your factoring company details.
        </h4>
        <form-field
          ref="companyName"
          label="Factoring Company Name (Required)"
          :value="company.name"
          placeholder="Enter Company Name"
          :on-input="onInput"
          :error="companyNameError"
          :on-blur="validateCompanyName"
          name="name"
        />
        <form-field
          ref="phone"
          class="mt-4"
          label="Company Phone # (Required)"
          :value="company.phone_number"
          placeholder="Enter phone #"
          :on-input="onInput"
          name="phone_number"
          :error="phoneError"
          :on-blur="validatePhone"
          pattern="number"
        />
        <form-field
          ref="email"
          class="mt-4"
          label="Company Email (Required)"
          :value="company.email"
          placeholder="Enter Email"
          :on-input="onInput"
          :error="email_address_error"
          :on-blur="checkEmailAddress"
          name="email"
        />

        <button-main
          :disabled="isButtonDisabled"
          @click="() => handleSubmit()"
        >
          Add new company
        </button-main>
        <button-main
          variant="secondary"
          class="mt-3"
          @click="$emit('close')"
        >
          Cancel
        </button-main>
      </div>
    </div>
    <div v-else>
      <div
        class="relative flex w-full flex-col items-start rounded bg-white p-8"
        :class="{ 'modal-position': success }"
      >
        <div class="mb-6 mt-4 flex">
          <img
            class="mr-2"
            src="@/assets/yellow-checkmark.svg"
            alt="yellow checkmark"
          />
          <h3 class="text-3xl text-dark-blue">Company Added</h3>
        </div>
        <button-main @click="handleNextStep">Continue</button-main>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapState } from 'vuex';

// Components
import Modal from '@/components/modals/Modal.vue';
import ButtonMain from '@/components/Button.vue';
import FormField from '@/components/FormField.vue';

// Common
import { formatPhone } from '@/common/validators';
import { toastResponse } from '@/common/handleServerResponse';
import stepOrder from '@/common/stepOrder';

// Mixin
import phoneField from '@/mixins/phoneField';
import fullnameAndEmailField from '@/mixins/fullnameAndEmailField';

export default {
  name: 'AddCompanyModal',
  components: {
    Modal,
    ButtonMain,
    FormField,
  },
  mixins: [phoneField, fullnameAndEmailField],
  props: {
    visible: Boolean,
    nextStep: Function,
    skipStep: Function,
    formPosition: Number,
  },
  emits: ['close'],
  data() {
    return {
      steps: [],
      company: {
        name: '',
        phone_number: '',
        email: '',
        paymentFactorId: null,
      },
      companyNameError: '',
      success: false,
    };
  },
  mounted() {
    this.steps = stepOrder;
  },
  computed: {
    isButtonDisabled() {
      const { name, phone_number, email } = this.company;
      return (
        this.companyNameError ||
        this.phoneError ||
        this.email_address_error ||
        !name ||
        !email ||
        !phone_number
      );
    },
    ...mapState({
      paymentTermId: (state) => state.payments?.payment_term_id || null,
      paymentBody: (state) => state.payments.payment_body || {},
      onboardingPacketId: (state) => state.onboardingPacket.id || null,
      onboardingId: (state) => state.carriers.carrierData.id || null,
      stageName: (state) => state.carriers.carrierData.stage_name || null,
      companyQuestionSections: (state) =>
        state.carriers.carrierData?.company_question_sections || null,
    }),
  },
  methods: {
    validateCompanyName(e) {
      this.companyNameError = '';
      const companyName = e.target?.value || e?.value || null;
      if (!companyName) {
        this.companyNameError = 'This field is required.';
        return;
      }
    },
    onInput(e) {
      if (e.target.name.includes('phone_number')) {
        const value = formatPhone(e.target.value);
        this.company[e.target.name] = value;
      } else {
        this.company[e.target.name] = e.target.value;
      }
    },
    async handleSubmit() {
      this.validateCompanyName(this.$refs.companyName);
      this.checkEmailAddress(this.$refs.email);
      this.validatePhone(this.$refs.phone);

      if (
        this.companyNameError ||
        this.phoneError ||
        this.email_address_error
      ) {
        return;
      }
      this.paymentFactorId = null;
      let body = {
        name: this.company.name,
        phone: this.company.phone_number,
        email_address: this.company.email,
      };
      try {
        const response = await this.$store.dispatch(
          'factors/createFactor',
          body
        );
        if ([200, 201].includes(response?.status) && response?.data) {
          this.paymentFactorId = response.data.id;
          this.success = true;
        } else {
          toastResponse(this, response, null);
          this.success = false;
        }
      } catch (error) {
        console.error('ERROR:', error);
        this.success = false;
      }
    },
    async updateOnboardingPacket(body) {
      await this.$store.dispatch('payments/setPaymentBody', body);
    },
    async customeUpdatePacket() {
      const firstTimeQuestionSectionIsSkipped =
        this.stageName === 'Custom Questions' &&
        this.steps[this.formPosition] === 'Contract-1';
      const canHitNextStep = firstTimeQuestionSectionIsSkipped;
      if (canHitNextStep) {
        // If no change was made to the onboarding packet, the next step should hit if available
        const nextStepRes = await this.$store.dispatch(
          'onboardingPacket/nextStepPatch',
          {
            id: this.onboardingId,
          }
        );
        return nextStepRes;
      }
    },
    async handleNextStep() {
      if (this.paymentTermId && this.paymentFactorId) {
        let body = {
          payment_term_id: this.paymentTermId,
          factor_id: this.paymentFactorId,
        };
        await this.updateOnboardingPacket(body);
        if (this.paymentBody) {
          await this.$store.dispatch('factors/assignFactor', {
            paymentBody: this.paymentBody,
            onboardingPacketId: this.onboardingPacketId,
          });
          if (this.companyQuestionSections.length) await this.nextStep(3);
          // Skip question section if there aren't questions for the current Broker.
          else {
            await this.nextStep(4);
            /* Because the question section is escaped, the following function (updatePacket) calls the nextStepPatch function simulating the click that would occur in the question section */

            // TODO - Jordan needs to validate the logic of this code
            this.customeUpdatePacket();
          }
        }
      } else {
        console.error('No payment method term or factor id detected.');
      }
      this.$emit('close');
    },
  },
  setup(_, { emit }) {
    const onCancel = () => {
      emit('close');
    };

    return {
      onCancel,
    };
  },
};
</script>
<style scoped>
.modal-position {
  top: -190px;
}
</style>
